import dayjs, { type Dayjs } from 'dayjs';

/**
 * Pure helpers behind `useCampaignDateRange`. Kept free of React/Redux/SWR so the
 * campaign-window business rule can be unit-tested in isolation.
 */

/** Minimal shape needed to pick the previous campaign's harvest date. */
export interface HarvestableCropMonitoring {
    id: number;
    harvestDate: string | null;
}

/**
 * Latest harvest date among the field's crop monitorings, excluding the current one.
 * Returns null when there is no previous harvested crop monitoring.
 */
export const pickPreviousHarvest = (cropMonitorings: HarvestableCropMonitoring[], currentId?: number): Dayjs | null => {
    const harvests = cropMonitorings
        .filter((cm) => cm.id !== currentId && cm.harvestDate)
        .map((cm) => dayjs(cm.harvestDate as string));
    if (!harvests.length) return null;
    return harvests.reduce((latest, current) => (current.isAfter(latest) ? current : latest));
};

/**
 * Campaign start:
 *   - the previous harvest when it falls on/after the PREVIOUS year's fallow-min
 *     (i.e. it belongs to the immediately preceding cycle);
 *   - otherwise the current fallow-min date.
 * Returns null while the fallow-min is still unknown.
 */
export const resolveCampaignStart = (fallowMin: Dayjs | null, previousHarvest: Dayjs | null): Dayjs | null => {
    if (!fallowMin) return null;
    const previousFallowMin = fallowMin.subtract(1, 'year');
    if (previousHarvest && !previousHarvest.isBefore(previousFallowMin, 'day')) {
        return previousHarvest;
    }
    return fallowMin;
};

/** Campaign end: the harvest date when there is one, otherwise far enough ahead to show everything. */
export const resolveCampaignEnd = (harvestDate: string | null | undefined): Dayjs =>
    harvestDate ? dayjs(harvestDate) : dayjs().add(10, 'year');
